import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Creates a singleton container that applies aria-live "polite" to itself and creates a portal for other
 * components such as tooltips. This is mainly used for a11y to read off the contents of a tooltip
 * due to the use of a live region on the container.
 *
 * How to use:
 * Call the hook inside your React component, take the returned container and wrap it around the children
 * you want to be portal-ed inside this singleton container.
 *
 * This hook shouldn't be used with Modals.
 *
 * @returns Singleton container with live region defined
 * @internal
 */
export const usePoliteContainer = () => {
  const [spanContainer, setSpanContainer] = useState<Element | null>(null);

  // Create our a11y live region container and append it to the document.body if it doesn't exist
  useEffect(() => {
    let portalContainer = document.querySelector('[data-polite-container]');

    if (portalContainer === null) {
      portalContainer = document.createElement('div');
      portalContainer.setAttribute('aria-live', 'polite');
      portalContainer.setAttribute('data-polite-container', 'true');
      portalContainer.setAttribute('data-testid', 'polite-container');
      document.body.appendChild(portalContainer);
    }

    // Actual children will be placed inside this span container which in turn will be placed in the polite container
    // This is done to track children inside the polite container that could be conditionally rendered
    const spanContainer = document.createElement('span');
    spanContainer.setAttribute('style', 'display: contents');
    setSpanContainer(spanContainer);

    portalContainer.appendChild(spanContainer);

    return () => {
      const politeContainer = document.querySelector('[data-polite-container]');
      // Cleanup and remove the span container from within politeContainer
      if (spanContainer !== null && politeContainer !== null) {
        politeContainer.removeChild(spanContainer);
      }
      // Cleanup and remove the polite container from document.body
      //  Check if there are no children before removing the container
      // (we wrapped the children in spans in case the children are conditionally rendered)
      if (politeContainer !== null && politeContainer.children.length === 0) {
        document.body.removeChild(politeContainer);
      }
    };
  }, []);

  // Create a portal component for the children to be placed inside
  const Portal = useMemo(
    () => (props: PropsWithChildren) => {
      const { children } = props;

      if (!spanContainer) {
        return null;
      }

      return <>{createPortal(children, spanContainer)}</>;
    },
    [spanContainer],
  );

  return Portal;
};
